import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share, SquarePlus } from "lucide-react";
import { useLanguage } from "@/i18n";

const KEY = "devisto_install_prompt";
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
const DELAY_MS = 8000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const InstallPrompt = () => {
  const { t } = useLanguage();
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    if (isStandalone() || window.innerWidth >= 768) return;
    const stored = Number(localStorage.getItem(KEY) || 0);
    if (stored === -1 || (stored > 0 && Date.now() < stored)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      localStorage.setItem(KEY, "-1");
      setOpen(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    const timer = setTimeout(() => setOpen(true), DELAY_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(timer);
    };
  }, []);

  const snooze = useCallback(() => {
    localStorage.setItem(KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    localStorage.setItem(KEY, outcome === "accepted" ? "-1" : String(Date.now() + SNOOZE_MS));
    setOpen(false);
  }, [deferred]);

  const ios = isIos();
  if (!open || (!ios && !deferred)) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="install-prompt"
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.96 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        data-testid="install-prompt"
        className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 left-4 z-[60] max-w-[22rem] sm:left-auto rounded-2xl border border-[#1B3573] bg-[#0B1E3D] p-4 text-white shadow-[0_24px_60px_-20px_rgba(8,16,77,0.65)] md:hidden"
      >
        <button
          type="button"
          onClick={snooze}
          data-testid="install-prompt-dismiss"
          aria-label={t("Plus tard")}
          className="absolute right-2.5 top-2.5 rounded-full p-1.5 text-[#9FB4F2] hover:bg-white/10 hover:text-white transition-colors duration-200"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <img
            src="/assets/favicon-192.png"
            alt="Devisto"
            width="44"
            height="44"
            className="h-11 w-11 shrink-0 rounded-xl ring-1 ring-white/15"
          />
          <div>
            <p className="font-heading text-[15px] font-bold leading-snug">
              {steps ? t("Ajoutez Devisto à votre écran d'accueil") : t("Installez Devisto sur votre téléphone")}
            </p>
            {!steps && (
              <p className="mt-1 text-[13px] leading-relaxed text-[#9FB4F2]">
                {t("Un accès direct depuis votre écran d'accueil, même sur chantier.")}
              </p>
            )}
          </div>
        </div>

        {steps ? (
          <ol data-testid="install-prompt-steps" className="mt-3.5 space-y-2.5 text-[13px] text-[#D5DEF8]">
            <li className="flex items-center gap-2.5">
              <Share className="h-4 w-4 shrink-0 text-[#9FB4F2]" strokeWidth={2.2} />
              {t("Appuyez sur Partager, en bas de Safari")}
            </li>
            <li className="flex items-center gap-2.5">
              <SquarePlus className="h-4 w-4 shrink-0 text-[#9FB4F2]" strokeWidth={2.2} />
              {t("Choisissez « Sur l'écran d'accueil »")}
            </li>
          </ol>
        ) : (
          <div className="mt-4 flex items-center gap-2.5">
            <button
              type="button"
              onClick={ios ? () => setSteps(true) : install}
              data-testid="install-prompt-cta"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#3A5CC8] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#3A5CC8] active:scale-[0.98] transition-all duration-200"
            >
              {ios ? t("Comment faire") : (<><Download className="h-4 w-4" /> {t("Installer")}</>)}
            </button>
            <button
              type="button"
              onClick={snooze}
              data-testid="install-prompt-later"
              className="rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[#9FB4F2] hover:text-white transition-colors duration-200"
            >
              {t("Plus tard")}
            </button>
          </div>
        )}

        {steps && (
          <button
            type="button"
            onClick={snooze}
            data-testid="install-prompt-steps-done"
            className="mt-4 w-full rounded-xl bg-white/10 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/15 transition-colors duration-200"
          >
            {t("C'est fait")}
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
